import { useEffect, useState, useRef, Dispatch, SetStateAction, useCallback, CSSProperties, useMemo } from 'react';
import { Modal, Button, ButtonGroup } from 'react-bootstrap';
import { FixedSizeList as VirtualList } from 'react-window';
import MobileDeviceDetailsForm from './MobileDeviceDetailsForm';
import context from '../Administration/context/shared';
import SetDevicePropertyForm from '../Administration/forms/SetDevicePropertyForm';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Delete, Message, Settings } from '@mui/icons-material';
import { Dropdown, Input, SelectPicker, Whisper, Popover } from 'rsuite';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Skeleton } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { Account, Device } from '../../shared/generated';
import { Usb } from '@mui/icons-material';
import { useBackButton } from '../../utils/backButton';
import { buildDeviceId } from '../../utils/deviceId';

interface Props {
    account: Account;
    setAccount: Dispatch<SetStateAction<Account>>;
    data: Device[];
    isLoading: boolean;
    onSave: (deviceId: string, updated: Partial<Device>) => void;
    onDelete: (devices: Device[]) => void;
    setCreateModalOpen: Dispatch<SetStateAction<boolean>>;
    setShowMessageDialog: Dispatch<SetStateAction<boolean>>;
}
const getRowStyle = (device: Device, selected: boolean) => {
    const banned = device.Banned;
    const blocked = device.Blocked;

    return {
        selectedHover: selected ? "var(--selected-row-hover)" : "",
        afterColor: selected ? "var(--selected-row)" : "",
        backgroundColor: banned ? "var(--banned)" : (blocked ? "var(--blocked)" : "var(--bg)"),
        background: banned ? "var(--banned)" : (blocked ? "var(--blocked)" : "var(--bg)"),
        color: banned || blocked ? "var(--text-dark)" : 'var(--text)',
        stroke: selected || banned || blocked ? "var(--text-dark)" : 'var(--text)',
    }
}
// расчёт дней до окончания средств: отрицательное значение — «Недостаточно средств» (-2) или «Нет абон. платы» (-1)
const getExpirationDays = (device: Device) => {
    if (device.Balance <= 0)
        return -2;
    if (device.Charge <= 0)
        return -1;
    return Math.ceil(device.Balance / device.Charge);
}
// поля, доступные для сортировки мобильного списка
const SORT_FIELDS: Record<string, { label: string; value: (d: Device) => string | number | null | undefined }> = {
    DeviceModelName: { label: 'Модель', value: d => d.DeviceModelName || null },
    Serial: { label: 'с/н', value: d => d.Serial || null },
    Balance: { label: 'Баланс', value: d => d.Balance },
    Cost: { label: 'Ц.Д.', value: d => d.Cost },
    Charge: { label: 'Аб. плата', value: d => d.Charge },
    LastDataTime: { label: 'Посл. данные', value: d => d.LastDataTime?.Seconds ?? null },
    Expiration: { label: 'Истекает', value: getExpirationDays },
};
const compareDevices = (a: Device, b: Device, field: string) => {
    const getter = SORT_FIELDS[field]?.value;
    if (!getter)
        return 0;
    const av = getter(a);
    const bv = getter(b);
    if (av == null && bv == null)
        return 0;
    if (av == null)
        return 1;
    if (bv == null)
        return -1;
    if (typeof av === 'string' || typeof bv === 'string')
        return String(av).localeCompare(String(bv), undefined, { numeric: true });
    return av - bv;
}
export default function MobileDevicesList(props: Props) {
    const { data, onSave, onDelete,
        setCreateModalOpen, isLoading,
        setShowMessageDialog } = props;

    const [selected, setSelected] = useState<Device | null>(null);
    const [highlighted, setHighlighted] = useState<Device[]>([]);
    const [show, setShow] = useState(false);
    const { foundDeviceToShow, setFoundDeviceToShow } = context;
    const listRef = useRef<VirtualList | null>(null);
    const [height, setHeight] = useState<number>(300);
    const open = (device: Device) => { setSelected(device); setShow(true); };
    const close = () => { setSelected(null); setShow(false); };
    const listContainerRef = useRef<HTMLDivElement>(null);

    // фильтры
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [showOnlyBanned, setShowOnlyBanned] = useState<boolean>(false);
    const [showOnlyBlocked, setShowOnlyBlocked] = useState<boolean>(false);
    const [selectedModel, setSelectedModel] = useState<number | null>(null);

    // сортировка (сохраняется между сессиями)
    const [sortField, setSortField] = useState<string | null>(() => localStorage.getItem('mobileDevicesSortField'));
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => localStorage.getItem('mobileDevicesSortDir') === 'desc' ? 'desc' : 'asc');
    useEffect(() => {
        if (sortField)
            localStorage.setItem('mobileDevicesSortField', sortField);
        else
            localStorage.removeItem('mobileDevicesSortField');
    }, [sortField]);
    useEffect(() => {
        localStorage.setItem('mobileDevicesSortDir', sortDir);
    }, [sortDir]);

    const models = useMemo(() => {
        const map = new Map<number, string>();
        data.forEach(d => map.set(d.Model, d.DeviceModelName));
        return Array.from(map.entries()).map(([model, name]) => ({ model, name }));
    }, [data]);

    const modelOptions = useMemo(() => {
        const opts = models.map(m => ({ label: m.name, value: m.model }));
        return [{ label: 'Все модели', value: null }, ...opts];
    }, [models]);


    const handleSave = (updated: Partial<Device>) => {
        if (!selected?.Serial) return;
        const deviceId = buildDeviceId(selected.Model, selected.Serial);
        onSave(deviceId, updated);
        close();
    };

    const handleDelete = () => {
        if (!selected) return;
        onDelete([selected]);
        close();
    };

    const handleDeleteMultiple = () => {
        if (highlighted.length == 0) return toast.info("Необходимо выбрать хотя бы одно устройство");
        onDelete(highlighted);
    }

    useEffect(() => {
        selectFoundDevice();
        sizeToFit();
    }, [data, searchKeyword, showOnlyBanned, showOnlyBlocked, selectedModel]);

    const sizeToFit = useCallback(() => {
        const newHeight = listContainerRef?.current?.getBoundingClientRect().height || 300;
        setHeight(Math.max(newHeight));
    }, []);

    useEffect(() => {
        window.addEventListener("resize", sizeToFit)
        return () => window.removeEventListener("resize", sizeToFit)
    }, [sizeToFit]);

    const handleClickDevice = (device: Device) => {
        if (highlighted.includes(device))
            setHighlighted(highlighted.filter(highlightedDevice => highlightedDevice != device))
        else
            setHighlighted(prev => [...prev, device]);
    }

    useBackButton(close, show);

    // фильтрация данных для виртуального списка
    const filteredData = useMemo(() => {
        let res = data;

        if (searchKeyword && searchKeyword.trim() !== '') {
            const kw = searchKeyword.trim().toLowerCase();
            res = res.filter(d =>
                (d.DeviceModelName || '').toLowerCase().includes(kw) ||
                (d.Serial || '').toLowerCase().includes(kw) ||
                (d.Imei || '').toLowerCase().includes(kw) ||
                (d.Comment || '').toLowerCase().includes(kw)
            );
        }

        if (showOnlyBanned) {
            res = res.filter(d => d.Banned === true);
        }

        if (showOnlyBlocked) {
            res = res.filter(d => d.Blocked === true);
        }

        if (selectedModel !== null && selectedModel !== undefined) {
            res = res.filter(d => d.Model === selectedModel);
        }

        if (sortField && SORT_FIELDS[sortField]) {
            res = [...res].sort((a, b) => {
                const cmp = compareDevices(a, b, sortField);
                return sortDir === 'desc' ? -cmp : cmp;
            });
        }

        // очистка подсветки от элементов, которых нет в результате фильтра
        setHighlighted(prev => prev.filter(h => res.includes(h)));

        return res;
    }, [data, searchKeyword, showOnlyBanned, showOnlyBlocked, selectedModel, sortField, sortDir]);

    const selectFoundDevice = () => {
        if (!foundDeviceToShow)
            return;

        const highlightedArray: Device[] = [];
        filteredData.forEach((device, idx) => {
            if (device.Model === foundDeviceToShow.deviceModel && device.Serial === foundDeviceToShow.deviceSerial) {
                highlightedArray.push(device);
                try {
                    listRef?.current?.scrollToItem(idx, 'center');
                    setFoundDeviceToShow(null);
                } catch {
                    // ignore
                }
            }
        });
        setHighlighted(highlightedArray);
    }

    const Row = ({ index, style }: { index: number; style: CSSProperties }) => {
        const device = filteredData[index];
        const lastData = device.LastDataTime ? new Date((device.LastDataTime?.Seconds ?? 0) * 1000 ).toLocaleString() : '';
        const deviceHighlighted = highlighted.includes(device);
        const cardStyle = getRowStyle(device, deviceHighlighted);

        const expirationDays = getExpirationDays(device);
        let expirationString = '';

        if (expirationDays === -2)
            expirationString = "Недостаточно средств";
        else if (expirationDays === -1)
            expirationString = "Нет абон. платы";
        else {
            let date = dayjs();
            date = date.add(expirationDays, 'd');
            expirationString = date.format("DD MMM YYYY");
        }

        return (
            <div className={highlighted.includes(device) && "selected-device" || ""}  style={{ ...style, padding: '0 .5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '.5rem', background: cardStyle?.background }}>
                <div style={{ color: cardStyle?.color, flex: 1 }} onClick={() => handleClickDevice(device)}>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '.35rem', flexWrap: 'wrap' }}>
                        {device.DeviceModelName}
                        <span style={{ fontWeight: 400 }}>
                            {device.Serial}
                        </span>
                        {device.Banned ? <span className="mobileDeviceBadge banned">Откл.</span> : null}
                        {device.Blocked ? <span className="mobileDeviceBadge blocked">Забл.</span> : null}
                        {device.TestMode ? <span className="mobileDeviceBadge test">Тест</span> : null}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '.25rem', fontSize: '.85rem' }}>
                        <div>Посл. данные: <b>{lastData}</b></div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '.25rem', fontSize: '.85rem' }}>
                        <div>Истекает: <b>{expirationString}</b></div>
                    </div>
                </div>
                <div>
                    <Button variant="custom" title="Открыть" onClick={() => open(device)}>Открыть</Button>
                </div>
            </div>
        );
    };

    const highlightAll = () => {
        if (highlighted.length > 0)
            setHighlighted([])
        else
            setHighlighted(filteredData)
    }

    const clearFilters = () => {
        setSearchKeyword('');
        setSelectedModel(null);
        setShowOnlyBanned(false);
        setShowOnlyBlocked(false);
        setSortField(null);
        setSortDir('asc');
    }

    const sortOptions = useMemo(() =>
        Object.entries(SORT_FIELDS).map(([field, def]) => ({ label: def.label, value: field })),
        []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <div className="mobileDevicesToolbar" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.5rem', alignItems: 'center', flex: '0 0 auto' }}>
                <div>
                    <Dropdown title="Действия">
                        <Dropdown.Item onClick={() => setCreateModalOpen(true)}>
                            <AddCircleOutlineIcon className="marginRight" />
                            Добавить устройство
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => context.setShowAccountSettings(true)}>
                            <Settings className="marginRight" />
                            Настройки аккаунта
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => { setShowMessageDialog(true); }}>
                            <Message className="marginRight"/>
                            Сообщение
                        </Dropdown.Item>
                        <SetDevicePropertyForm
                            title="Заблокировать устройства"
                            propertyName="Blocked"
                            label="Блокировка"
                            devices={highlighted}
                            isMobile={true}
                        />
                        <SetDevicePropertyForm
                            title="Отключить устройства"
                            propertyName="Banned"
                            label="Отключение"
                            devices={highlighted}
                            isMobile={true}
                        />
                        <SetDevicePropertyForm
                            title="Установить цену дилера"
                            propertyName="Cost"
                            label="Цена дилера"
                            devices={highlighted}
                            isMobile={true}
                        />
                        <SetDevicePropertyForm
                            title="Установить абонентскую плату в месяц"
                            propertyName="Charge"
                            label="Аб. плата в месяц"
                            devices={highlighted}
                            isMobile={true}
                        />
                        <SetDevicePropertyForm
                            title="Установить баланс"
                            propertyName="Balance"
                            label="Баланс"
                            devices={highlighted}
                            isMobile={true}
                        />
                        <SetDevicePropertyForm
                            title="Тестовый режим"
                            propertyName="TestMode"
                            label="Тестовый режим"
                            devices={highlighted}
                            isMobile={true}
                        />
                        <Dropdown.Item onClick={() => handleDeleteMultiple()}>
                            <Delete className="marginRight" />
                            Удалить устройства
                        </Dropdown.Item>
                    </Dropdown>
                </div>
                <Whisper
                    placement="bottom"
                    trigger="click"
                    speaker={
                        <Popover style={{ width: 'calc(100% - 2rem)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                                <Input value={searchKeyword} onChange={setSearchKeyword} placeholder="Поиск: модель / серийный / IMEI / комментарий" />
                                <SelectPicker
                                    data={modelOptions}
                                    value={selectedModel}
                                    onChange={(v) => setSelectedModel(v as number | null)}
                                    placeholder="Все модели"
                                    cleanable
                                    w={ "100%"}
                                    style={{ width: '100%' }}
                                    searchable={false}
                                    menuMaxHeight={200}
                                />
                                <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                                    <SelectPicker
                                        data={sortOptions}
                                        value={sortField}
                                        onChange={(v) => setSortField(v as string | null)}
                                        placeholder="Без сортировки"
                                        cleanable
                                        w={"100%"}
                                        style={{ width: '100%' }}
                                        searchable={false}
                                        menuMaxHeight={200}
                                    />
                                    <Button
                                        variant="custom"
                                        disabled={!sortField}
                                        title={sortDir === 'asc' ? 'По возрастанию' : 'По убыванию'}
                                        onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                                        style={{ padding: '.35rem .6rem', flexShrink: 0 }}
                                    >
                                        {sortDir === 'asc' ? '↑' : '↓'}
                                    </Button>
                                </div>
                                <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
                                    <Button variant={showOnlyBanned ? "primary" : "custom"} onClick={() => setShowOnlyBanned(prev => !prev)} style={{ padding: '.35rem .6rem' }}>Откл.</Button>
                                    <Button variant={showOnlyBlocked ? "primary" : "custom"} onClick={() => setShowOnlyBlocked(prev => !prev)} style={{ padding: '.35rem .6rem' }}>Забл.</Button>
                                    <Button variant="custom" onClick={clearFilters} style={{ padding: '.35rem .6rem' }}>Сброс</Button>
                                </div>
                            </div>
                        </Popover>
                    }
                >
                    <Button variant="custom" title="Фильтры">
                        <TuneIcon />
                    </Button>
                </Whisper>

                <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>

                    <ButtonGroup>
                        <Button variant="custom" onClick={highlightAll}>{highlighted.length > 0 ? `Сбросить выделение (${highlighted.length})` : `Отметить все`}</Button>
                    </ButtonGroup>
                </div>
            </div>

            <div className="mobileDevicesListTitle">Контроллеры</div>
            <div ref={listContainerRef} style={{ flex: 1, minHeight: 0, border: '1px solid var(--border)', borderRadius: '.5rem', overflow: 'hidden' }}>
                {
                    isLoading ?
                        <Skeleton height={height} />
                        : filteredData.length === 0 ?
                            <div className="mobileClientsEmptyState">
                                <Usb className="mobileClientsEmptyStateIcon" />
                                <span>{data.length === 0 ? 'Нет контроллеров' : 'Ничего не найдено'}</span>
                            </div>
                            :
                            <VirtualList
                                ref={listRef}
                                height={height}
                                itemCount={filteredData.length}
                                itemSize={84}
                                width={'100%'}
                            >
                                {Row}
                            </VirtualList>
                }

            </div>

            <Modal show={show} onHide={close} centered className="mobileBottomSheet">
                <Modal.Header closeButton>
                    <Modal.Title>Детали устройства</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selected && <MobileDeviceDetailsForm device={selected} onCancel={close} onSave={handleSave} onDelete={handleDelete} />}
                </Modal.Body>
            </Modal>
        </div>
    );
}